import * as React from 'react';
import styled from 'styled-components';

import { InputError } from './InputError';
import { animated, useSpring } from 'react-spring';

import { ITimeItem } from '../context/FormContextProvider';

interface ITimesWrapper {
  loading: boolean;
  available: Array<ITimeItem>;
  selected: string | null;
  timesError: string;
  showCount: boolean;
  onSelect: (item: ITimeItem) => void;
}

export const TimesWrapper: React.FC<ITimesWrapper> = React.memo(
  ({ loading, available, selected, timesError, showCount, onSelect }) => {
    const [styles, api] = useSpring(() => ({
      opacity: 0,
      display: 'none',
    }));

    React.useEffect(() => {
      if (!loading && available.length > 0) {
        api.start({
          opacity: 1,
          display: 'inline-grid',
        });
      }
    }, [loading, available]);

    return (
      <div style={{ marginTop: '10px' }}>
        {timesError ? <InputError>{timesError}</InputError> : null}
        <StyledCenteringTimesWrapper>
          {!loading && available.length === 0 ? (
            <p>Ingen tidspunkt er ledig for denne visningen.</p>
          ) : null}
          <StyledTimesWrapper style={styles}>
            {!loading && available.length
              ? available?.map((item, index: number) => (
                  <TimesItem
                    key={`time_${item.from}_${index}`}
                    time={`${item.from} - ${item.to}`}
                    available={item.availableSlots}
                    firstWithNoParticipants={item.firstWithNoParticipants}
                    selected={selected === item.from}
                    showCount={showCount}
                    onClick={() => {
                      if (typeof onSelect === 'function') {
                        onSelect(item);
                      }
                    }}
                  />
                ))
              : null}
          </StyledTimesWrapper>
        </StyledCenteringTimesWrapper>
      </div>
    );
  }
);

const StyledCenteringTimesWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const StyledTimesWrapper = styled(animated.div)`
  grid-template-columns: 115px 115px 115px;
  grid-column-gap: 5px;
  grid-row-gap: 5px;
  justify-content: center;

  @media all and (max-width: 299px) {
    grid-template-columns: 115px;
    grid-column-gap: 5px;
    grid-row-gap: 5px;
  }

  @media all and (min-width: 300px) and (max-width: 500px) {
    grid-template-columns: 115px 115px;
    grid-column-gap: 5px;
    grid-row-gap: 5px;
  }
`;

interface ITimesItem {
  time: string;
  available: number;
  firstWithNoParticipants: boolean;
  selected: boolean;
  showCount: boolean;
  onClick: () => void;
}

const TimesItem: React.FC<ITimesItem> = ({
  time,
  available,
  firstWithNoParticipants,
  selected,
  showCount,
  onClick,
}) => {
  return (
    <StyledTimesItem
      onClick={onClick}
      className={selected ? 'selected-time-item' : ''}
    >
      <span className={!showCount ? 'available-hidden' : ''}>{time}</span>
      {showCount ? (
        <span className="available">Ledige plasser: {available}</span>
      ) : null}
      <span
        className={
          firstWithNoParticipants
            ? 'no-participants-indicator active'
            : 'no-participants-indicator'
        }
      >
        {firstWithNoParticipants ? (
          <svg
            width="200px"
            height="6"
            viewBox="0 0 200 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              width="100%"
              height="6"
              fill="url(#viewing_gradient_identicator)"
            />
            <defs>
              <linearGradient
                id="viewing_gradient_identicator"
                x1="2.5"
                y1="20.75"
                x2="10.3136"
                y2="-20.6383"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#834628" />
                <stop offset="0.34375" stopColor="#824528" />
                <stop offset="0.572917" stopColor="#D7A87C" />
                <stop offset="0.791667" stopColor="#9C603F" />
                <stop offset="1" stopColor="#9A5E3D" />
              </linearGradient>
            </defs>
          </svg>
        ) : null}
      </span>
    </StyledTimesItem>
  );
};

const StyledTimesItem = styled.div`
  display: inline-block;
  width: 115px;
  background: #4b4b4b;
  font-size: 16px;
  text-align: center;
  color: #fff;
  user-select: none;

  &.selected-time-item {
    background: ${({ theme }) => theme.colors.primary};
    color: #000;
  }

  .available {
    font-size: 11px;
    padding: 2px 10px 10px 10px;
  }

  span {
    position: relative;
    display: block;
    width: 100%;
    &:first-child {
      padding: 10px 10px 2px 10px;
    }

    &.no-participants-indicator {
      position: relative;
      width: 100%;
      height: 5px;
      background: #626262;
      overflow: hidden;

      svg {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
      }
    }
  }

  .available-hidden {
    padding: 10px !important;
  }

  &:hover {
    cursor: pointer;
  }

  @media all and (max-width: 400px) {
    flex-flow: row wrap;
  }
`;
